import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { STAGES, STAGE_COLORS } from "@/lib/i18n";
import { Users, Briefcase, PhoneCall, Trophy, TrendingUp, Inbox, Package, Globe2 } from "lucide-react";
import MissedCallsPanel from "@/components/MissedCallsPanel";

const KpiCard = ({ icon: Icon, label, value, testid, accent = "#0052CC" }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</div>
        <div className="mt-2 font-heading text-3xl font-bold text-slate-900 tracking-tight">{value}</div>
      </div>
      <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}15`, color: accent }}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [stats, setStats] = useState(null);
  const [repKpis, setRepKpis] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    if (user?.role === "company_owner") {
      api.get("/dashboard/rep-kpis").then((r) => setRepKpis(r.data)).catch(() => {});
    }
  }, [user]);

  if (!stats) return <div className="text-slate-500">{t("common.loading")}</div>;

  const maxStage = Math.max(...Object.values(stats.stage_counts || {}), 1);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("dashboard.title")}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1" data-testid="dashboard-title">
            {t("dashboard.welcome")}, {user?.name?.split(" ")[0]}
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <KpiCard icon={Users} label={t("dashboard.total_contacts")} value={stats.totals.contacts} testid="kpi-contacts" />
        <KpiCard icon={Briefcase} label={t("dashboard.total_opportunities")} value={stats.totals.opportunities} testid="kpi-opportunities" accent="#06B6D4" />
        <KpiCard icon={PhoneCall} label={t("dashboard.total_calls")} value={stats.totals.calls} testid="kpi-calls" accent="#8B5CF6" />
        <KpiCard icon={Trophy} label={t("dashboard.deals_won")} value={stats.totals.deals_won} testid="kpi-won" accent="#16A34A" />
        <KpiCard icon={TrendingUp} label={t("dashboard.conversion_rate")} value={`${stats.conversion_rate}%`} testid="kpi-conversion" accent="#F59E0B" />
        <KpiCard icon={Package} label={t("dashboard.pipeline_value")} value={`€${Math.round(stats.total_pipeline_value).toLocaleString()}`} testid="kpi-pipeline-value" accent="#0052CC" />
        <KpiCard icon={Trophy} label={t("dashboard.won_value")} value={`€${Math.round(stats.won_value).toLocaleString()}`} testid="kpi-won-value" accent="#10B981" />
        <KpiCard icon={Inbox} label={t("dashboard.new_inquiries")} value={stats.totals.new_inquiries} testid="kpi-inquiries" accent="#F97316" />
      </div>

      {/* Missed calls needing follow-up (auto-hidden if none) */}
      <MissedCallsPanel L={lang === "el"} testidPrefix="dashboard-missed" />

      {/* Funnel */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("dashboard.funnel")}</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">Pipeline funnel</h2>
          </div>
        </div>
        <div className="space-y-3" data-testid="funnel-section">
          {STAGES.map((s) => {
            const c = stats.stage_counts[s] || 0;
            const pct = Math.round((c / maxStage) * 100);
            return (
              <div key={s} className="flex items-center gap-4">
                <div className="w-44 text-sm font-medium text-slate-700">{t(`stage.${s}`)}</div>
                <div className="flex-1 h-7 bg-slate-100 rounded overflow-hidden relative">
                  <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: STAGE_COLORS[s] }} />
                </div>
                <div className="w-12 text-right text-sm font-semibold text-slate-900">{c}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Country performance */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-6">
          <Globe2 className="w-4 h-4 text-slate-500" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("dashboard.country_performance")}</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">By country</h2>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="country-perf-table">
            <thead>
              <tr className="text-left border-b border-slate-200">
                <th className="py-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("common.country")}</th>
                <th className="py-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Deals</th>
                <th className="py-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Won</th>
                <th className="py-2 text-right text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("common.value")}</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(stats.country_performance).length === 0 ? (
                <tr><td colSpan={4} className="py-6 text-center text-slate-400">{t("common.no_data")}</td></tr>
              ) : (
                Object.entries(stats.country_performance).map(([c, v]) => (
                  <tr key={c} className="border-b border-slate-100">
                    <td className="py-3 font-medium text-slate-900">{c}</td>
                    <td className="py-3 text-slate-700">{v.deals}</td>
                    <td className="py-3 text-emerald-700 font-semibold">{v.won}</td>
                    <td className="py-3 text-right font-semibold text-slate-900">€{Math.round(v.value).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rep KPIs (owner) */}
      {user?.role === "company_owner" && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("dashboard.rep_kpis")}</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-6">Sales rep performance</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rep-kpis-table">
              <thead>
                <tr className="text-left border-b border-slate-200">
                  <th className="py-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Rep</th>
                  <th className="py-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Calls</th>
                  <th className="py-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Duration (min)</th>
                  <th className="py-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Opps</th>
                  <th className="py-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Won</th>
                  <th className="py-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Lost</th>
                  <th className="py-2 text-right text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Conv. %</th>
                </tr>
              </thead>
              <tbody>
                {repKpis.length === 0 ? (
                  <tr><td colSpan={7} className="py-6 text-center text-slate-400">{t("common.no_data")}</td></tr>
                ) : (
                  repKpis.map((r) => (
                    <tr key={r.rep.id} className="border-b border-slate-100">
                      <td className="py-3 font-medium text-slate-900">{r.rep.name}</td>
                      <td className="py-3">{r.calls}</td>
                      <td className="py-3">{r.call_duration}</td>
                      <td className="py-3">{r.opportunities}</td>
                      <td className="py-3 text-emerald-700 font-semibold">{r.deals_won}</td>
                      <td className="py-3 text-red-600">{r.deals_lost}</td>
                      <td className="py-3 text-right font-semibold">{r.conversion_rate}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
